import { decryptData } from './encryption';
import { addAuditLog } from './auditLog';

/**
 * Restore data from a backup
 */
export const restoreFromBackup = async (backupData: string): Promise<boolean> => {
  try {
    // Decrypt the backup
    const decryptedBackup = decryptData(backupData);
    
    // Parse the backup
    const backup = JSON.parse(decryptedBackup);
    
    // Validate backup format
    if (!backup.version || !backup.timestamp || !backup.data) {
      throw new Error('Ungültiges Backup-Format');
    }
    
    // Extract data
    const { buildings, archivedBuildings, konzernIds, photos, users, auditLogs } = backup.data;
    
    // Restore buildings
    if (buildings) {
      localStorage.setItem('installtrack_buildings', JSON.stringify(buildings));
    }
    
    // Restore archived buildings
    if (archivedBuildings) {
      localStorage.setItem('installtrack_archived_buildings', JSON.stringify(archivedBuildings));
    }
    
    // Restore Konzern IDs
    if (konzernIds) {
      localStorage.setItem('installtrack_konzern_ids', JSON.stringify(konzernIds));
    }
    
    // Restore photos
    if (photos) {
      localStorage.setItem('installtrack_photos', JSON.stringify(photos));
    }
    
    // Restore users (don't overwrite existing users)
    if (users) {
      const existingUsersJson = localStorage.getItem('installtrack_users');
      if (!existingUsersJson) {
        localStorage.setItem('installtrack_users', JSON.stringify(users));
      }
    }
    
    // Log the restore action
    addAuditLog('import', 'system', `System restored from backup created on ${new Date(backup.timestamp).toLocaleString()}`);
    
    return true;
  } catch (error) {
    console.error('Error restoring from backup:', error);
    throw new Error('Fehler beim Wiederherstellen des Backups');
  }
};

/**
 * Restore from a backup file
 */
export const restoreFromFile = async (file: File): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = async (event) => {
      try {
        if (!event.target?.result) {
          reject(new Error('Fehler beim Lesen der Datei'));
          return;
        }
        
        const backupData = event.target.result as string;
        const success = await restoreFromBackup(backupData);
        resolve(success);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Fehler beim Lesen der Datei'));
    };
    
    reader.readAsText(file);
  });
};

/**
 * Check if an automatic backup exists
 */
export const hasAutomaticBackup = (): boolean => {
  return !!localStorage.getItem('installtrack_auto_backup');
};

/**
 * Restore from the latest automatic backup
 */
export const restoreFromAutomaticBackup = async (): Promise<boolean> => {
  try {
    const autoBackup = localStorage.getItem('installtrack_auto_backup');
    if (!autoBackup) {
      throw new Error('Kein automatisches Backup verfügbar');
    }
    
    return await restoreFromBackup(autoBackup);
  } catch (error) {
    console.error('Error restoring from automatic backup:', error);
    throw error;
  }
};